import { getAPIHost } from '@rsos/utils/metaTags';

/**
 * Ask Scorpius for the WorkOS authorization URL.
 * @param {string} email - The email used to find the SSO organization
 * @param {string} redirectURI - The registered callback WorkOS returns to
 * @returns {Promise<{authorizationURL: string | undefined, state: string | undefined}>}
 *   Scorpius also returns the WorkOS state it bound to its state cookie, so
 *   callers can key per-attempt launch context to that same value.
 */
export const requestSSOAuthorization = async (email, redirectURI) => {
  // Business rule: callers may pass plus-address emails. URLSearchParams
  // keeps legacy /sso behavior while encoding them safely.
  const queryParams = new URLSearchParams({
    email,
    redirect_uri: redirectURI,
  });

  const resp = await fetch(
    `${getAPIHost()}/v1/scorpius/sso/authenticate?${queryParams.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );
  const data = await resp.json();
  return {
    // Passed through unchanged so the default export below keeps its exact
    // legacy return value.
    authorizationURL: data?.authorization_url,
    // New field with no legacy consumer, so it is normalized: anything other
    // than a non-empty string means "no state" and callers fail closed.
    state:
      typeof data?.state === 'string' && data.state ? data.state : undefined,
  };
};

/**
 * Legacy entry point used by /sso. Returns only the authorization URL, exactly
 * as before requestSSOAuthorization existed; callers that need the WorkOS
 * state (iRP SSO) use requestSSOAuthorization directly.
 * @param {string} email - The email used to find the SSO organization
 * @param {string} redirectURI - The registered callback WorkOS returns to
 * @returns {Promise<string | undefined>} The WorkOS authorization URL
 */
const authenticateWithSSO = async (email, redirectURI) => {
  const { authorizationURL } = await requestSSOAuthorization(
    email,
    redirectURI,
  );
  return authorizationURL;
};

export default authenticateWithSSO;
